import { Grid, makeStyles, Paper } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import { Autocomplete } from '@material-ui/lab';
import React, { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';

import FormTextFieldRedesign from 'retired/FormTextFieldRedesign';
import LocationSelectorRedesign from 'retired/LocationSelectorRedesign';
import { useDebounce } from 'utils/useDebounce';
import { Nonprofit, useSearchNonprofitsQuery } from 'generated/graphql';
import { showErrorAction } from 'containers/Notification/actions';

import Option from './Option';
import { SelectedNonprofit } from './SelectedNonprofit';
import { filterNonprofits } from './filterNonprofits';

interface Props {
  input: {
    onChange: (value: Nonprofit | null) => void;
    value?: Nonprofit;
  };
  meta: any;
  allowDeselection: boolean;
  required?: boolean;
  initialLocation?: string;
  supportedCountries?: string[];
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  inputComponent?: typeof FormTextFieldRedesign;
  locationSelectorPlaceholder?: string;
  locationSelectorHelperText?: string;
  nonprofitSelectorLabel?: string;
  nonprofitSelectorPlaceholder?: string;
  nonprofitSelectorHelperText?: string;
  nonprofitSelectorNoOptions?: string;
  extraContent?: ReactNode;
  onLocationSelect?: (loc: string) => void;
}

export const useStyles = makeStyles(() => ({
  noOptions: { display: 'none' },
}));

/**
 * This component is essentially copy-pasta of FormNonprofitsSelectorRedesign,
 * but made to work with nonprofits db.
 * So far it only supports selecting one item.
 */
export const FormNonprofitDbSelector = ({
  input,
  meta,
  allowDeselection = true,
  initialLocation,
  required = false,
  supportedCountries,
  variant = 'outlined',
  fullWidth = false,
  inputComponent: InputComponent = FormTextFieldRedesign,
  locationSelectorPlaceholder,
  locationSelectorHelperText = locationSelectorPlaceholder,
  nonprofitSelectorLabel = 'Nonprofit name',
  nonprofitSelectorPlaceholder = 'Search by nonprofit name or EIN',
  nonprofitSelectorHelperText = nonprofitSelectorPlaceholder,
  nonprofitSelectorNoOptions = 'No nonprofits found',
  extraContent,
  onLocationSelect,
}: Props) => {
  const hideNoOptionsClasses = useStyles();
  const [locationSelectorTouched, setLocationSelectorTouched] = useState(false);
  const [nonprofitsSelectorTouched, setNonprofitsSelectorTouched] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);

  const debouncedSearchTerm = useDebounce(searchTerm, 333);
  const dispatch = useDispatch();
  const { data, loading, error } = useSearchNonprofitsQuery({
    variables: {
      searchTerm: debouncedSearchTerm,
      countryCode: selectedLocation,
    },
    skip: debouncedSearchTerm.length < 3,
    onError: () => {
      dispatch(showErrorAction('An error occurred during saving, please try again.'));
      Sentry.captureException(error, {
        level: 'error',
      });
    },
  });

  const options = filterNonprofits(data?.searchNonprofits || []);

  const isOutlined = variant === 'outlined';

  const nonprofit = input.value;

  return (
    <Grid>
      <>
        <Grid container spacing={3}>
          <Grid item md={fullWidth ? 12 : 6}>
            <LocationSelectorRedesign
              required={required}
              onSelect={(location: string) => {
                onLocationSelect?.(location);
                setSelectedLocation(location);
                setLocationSelectorTouched(true);
              }}
              onBlur={() => setLocationSelectorTouched(true)}
              variant={variant}
              inputComponent={InputComponent}
              passedCountries={supportedCountries}
              initialLocation={initialLocation}
              placeholder={locationSelectorPlaceholder}
              helperText={locationSelectorHelperText}
              meta={{
                touched: nonprofitsSelectorTouched || locationSelectorTouched,
                error: selectedLocation === null || !selectedLocation || undefined,
              }}
            />
          </Grid>
          <Grid item md={fullWidth ? 12 : 6}>
            <Autocomplete
              openOnFocus={Boolean(searchTerm)}
              disableClearable
              disablePortal
              noOptionsText={nonprofitSelectorNoOptions}
              onChange={(_event, currentNonprofit) => {
                input.onChange(currentNonprofit);
              }}
              getOptionLabel={(option) => option?.name || ''}
              loading={loading}
              placeholder={nonprofitSelectorPlaceholder}
              renderInput={(params) => (
                <InputComponent
                  {...params}
                  required={required}
                  label={nonprofitSelectorLabel}
                  onBlur={() => setNonprofitsSelectorTouched(true)}
                  onFocus={() => setNonprofitsSelectorTouched(false)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  variant={variant}
                  InputLabelProps={isOutlined ? { shrink: true } : undefined}
                  meta={meta}
                  alwaysDisplayErrors={nonprofitsSelectorTouched}
                  helperText={nonprofitSelectorHelperText}
                />
              )}
              renderOption={(option) => {
                // @NOTE-RT: This is the only way to have clickable elements in your `nonprofitSelectorNoOptions` node per https://stackoverflow.com/a/63806699
                if (!option || option === nonprofitSelectorNoOptions) {
                  return nonprofitSelectorNoOptions;
                }

                return <Option nonprofit={option} key={option.nonprofitId || 0} />;
              }}
              variant={variant}
              // @NOTE-DP: when there's no noOptions text, we want to hide the noOptions container as well
              classes={nonprofitSelectorNoOptions ? undefined : hideNoOptionsClasses}
              PaperComponent={(paperProps) => (
                <Paper {...paperProps} square={!isOutlined}>
                  {paperProps.children}
                  {debouncedSearchTerm && extraContent}
                </Paper>
              )}
              options={options || []}
              filterOptions={(nonprofitSelectorOptions) => {
                if (loading || !searchTerm) {
                  return [];
                }
                return nonprofitSelectorOptions;
              }}
            />
          </Grid>
        </Grid>
      </>

      {nonprofit && (
        <SelectedNonprofit
          selectedNonprofit={nonprofit}
          allowDeselection={allowDeselection}
          isOutlined={isOutlined}
          onClear={() => {
            input.onChange(null);
          }}
        />
      )}
    </Grid>
  );
};
